import './teams.scss';

import { inviteTeamMember, resendTeamMemberInvite, updateTeamMemberRole } from '@api';
import { revokeTeamMemberInvite } from '@api/team';
import { Button, Dropdown, FormInput, Modal } from '@basqet-dashboard/common/components';
import { queryClient } from '@basqet-dashboard/common/config';
import { useFetchRoles } from '@basqet-dashboard/common/hooks/queries';
import { formatErrorMessage, inviteTeamMemberSchema } from '@basqet-dashboard/common/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

const InviteTeamMember = ({ closeModal, teamMember }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: teamMember?.email || '',
      first_name: teamMember?.first_name || '',
      last_name: teamMember?.last_name || '',
      role_id: teamMember?.role_id || ''
    },
    resolver: yupResolver(inviteTeamMemberSchema)
  });

  const { roles } = useFetchRoles();
  const [resendEmailLoading, setResendEmailLoading] = useState(false);
  const [revokeInviteLoading, setRevokeInviteLoading] = useState(false);
  const formattedRoles = roles.map((role) => ({ label: role.name, value: role.id }));

  const onSubmit = async (data) => {
    try {
      if (teamMember) {
        await updateTeamMemberRole({ roleId: data.role_id, userId: teamMember.id });
        toast.success('Team member updated successfully');
      } else {
        await inviteTeamMember(data);
        toast.success('Team member invited successfully');
      }
      queryClient.invalidateQueries('teamMembers');
      reset();
      closeModal();
    } catch (error) {
      formatErrorMessage(error);
    }
  };

  const handleResendInvite = async () => {
    setResendEmailLoading(true);
    try {
      await resendTeamMemberInvite(teamMember.id); // The id maps to the token of the invited team member
      toast.success('Invitation resent successfully');
    } catch (error) {
      formatErrorMessage(error);
    }
    setResendEmailLoading(false);
  };

  const handleRevokeInvite = async () => {
    setRevokeInviteLoading(true);
    try {
      await revokeTeamMemberInvite(teamMember.id); // The id maps to the token of the invited team member
      toast.success('Invite revoked successfully');
      closeModal();
      queryClient.invalidateQueries('teamMembers');
    } catch (error) {
      formatErrorMessage(error);
    }
    setRevokeInviteLoading(false);
  };

  return (
    <Modal
      onModalClose={closeModal}
      header={{
        name: teamMember ? 'Edit' : 'Invite new',
        cursive: 'team member'
      }}>
      <div className="modal__form">
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            placeholder="joan@your-company.co"
            label="Team member email"
            error={errors.email?.message}
            register={register('email')}
            readOnly={Boolean(teamMember)} // email not editable when editing
          />
          <FormInput
            placeholder="Angelina"
            label="First Name"
            error={errors.first_name?.message}
            register={register('first_name')}
            readOnly={Boolean(teamMember)} // first name not editable when editing
          />
          <FormInput
            placeholder="Jolie"
            label="Last Name"
            error={errors.last_name?.message}
            register={register('last_name')}
            readOnly={Boolean(teamMember)} // last name not editable when editing
          />

          <Dropdown
            formLabel="Role"
            options={formattedRoles}
            defaultValue={teamMember?.role_id}
            name="role_id"
            register={register('role_id')}
            placeholder="Select Role"
            error={errors.role_id?.message}
          />

          <Button isFull type="submit" isLoading={isSubmitting} disabled={!isDirty || !isValid}>
            {teamMember ? 'Save Changes' : 'Send Invite'}
          </Button>
        </form>

        <div className="modal__actions">
          {teamMember && teamMember.status.toLowerCase() === 'inactive' && (
            <Button
              isFull
              className="btn btn--resend"
              disabled={resendEmailLoading}
              isLoading={resendEmailLoading}
              onClick={handleResendInvite}>
              Resend Invitation
            </Button>
          )}

          {teamMember && teamMember.status.toLowerCase() === 'inactive' && (
            <Button
              isFull
              className="btn btn--remove"
              isLoading={revokeInviteLoading}
              disabled={revokeInviteLoading}
              onClick={handleRevokeInvite}>
              Revoke Invitation
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

InviteTeamMember.propTypes = {
  closeModal: PropTypes.func,
  teamMember: PropTypes.object
};

export { InviteTeamMember };
